import { usePage } from "@inertiajs/react";
import { AspectRatio, Box, Center, Loader } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { PhoneFrame } from "./PhoneFrame";

export const RENDER_OVERLAY_STYLE = {
  position: "absolute",
  inset: 0,
  zIndex: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "inherit",
  backgroundColor: "rgba(255, 255, 255, 0.62)",
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
  pointerEvents: "none",
};

function AdPreviewTmp({
  singleAdQuery,
  colorScheme = "light",
  withFrame = true,
  isRendering = false,
  overlayWhileLoading = false,
  fitToWidth = false,
}) {
  const { formatType: pageFormatType } = usePage().props;
  const adBannerFormat = singleAdQuery?.data?.banner_format
    ?? singleAdQuery?.data?.ad_banner_formats?.[0]?.banner_format;
  const formatType = adBannerFormat?.alias ?? pageFormatType ?? "medium_rectangle";

  const [width, setWidth] = useState(300);
  const [height, setHeight] = useState(250);

  useEffect(() => {
    if (adBannerFormat?.width && adBannerFormat?.height) {
      setWidth(adBannerFormat.width);
      setHeight(adBannerFormat.height);
    } else if (formatType == "halfpage") {
      setWidth(300);
      setHeight(600);
    } else if (formatType == "billboard") {
      setWidth(800);
      setHeight(250);
    } else if (formatType == "billboard-970-250") {
      setWidth(970);
      setHeight(250);
    } else {
      setWidth(300);
      setHeight(250);
    }
  }, [formatType, adBannerFormat?.width, adBannerFormat?.height]);

  const buildSrc = (baseUrl, scheme) => {
    if (!baseUrl) return undefined;
    try {
      const u = new URL(baseUrl, window.location.origin);
      u.searchParams.set("preview_color_scheme", scheme);
      return u.toString();
    } catch {
      const sep = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${sep}preview_color_scheme=${scheme}`;
    }
  };

  const src = useMemo(
    () => buildSrc(singleAdQuery?.data?.ad_preview_simple, colorScheme),
    [singleAdQuery?.data?.ad_preview_simple, colorScheme]
  );

  const mountStamp = useRef(new Date().getTime()).current;
  const [reloadNonce, setReloadNonce] = useState(0);
  const wasRefetching = useRef(false);

  useEffect(() => {
    const isRefetching = Boolean(singleAdQuery?.isRefetching);
    if (wasRefetching.current && !isRefetching) {
      setReloadNonce((nonce) => nonce + 1);
    }
    wasRefetching.current = isRefetching;
  }, [singleAdQuery?.isRefetching]);

  const iframeSrc = !src
    ? null
    : `${src}${src.includes('?') ? '&' : '?'}cacheId=${mountStamp}-${reloadNonce}`;

  const [iframeReady, setIframeReady] = useState(false);
  useEffect(() => { setIframeReady(false); }, [iframeSrc]);

  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const fit = () => setScale(stage.offsetWidth ? Math.min(1, stage.offsetWidth / width) : 1);

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(stage);

    return () => observer.disconnect();
  }, [fitToWidth, width]);

  const iframeElement = !iframeSrc ? null : (
    <iframe
      scrolling="no"
      src={iframeSrc}
      frameBorder="0"
      onLoad={() => setIframeReady(true)}
      style={{
        border: 0,
        colorScheme: colorScheme,
        ...(fitToWidth ? {
          width,
          height,
          display: 'block',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        } : null),
      }}
    />
  );

  const iframePreview = !iframeElement ? null : (fitToWidth ? (
    <Center h={height}>
      <Box style={{ width: width * scale, height: height * scale, overflow: 'hidden' }}>
        {iframeElement}
      </Box>
    </Center>
  ) : (
    <AspectRatio ratio={width / height}>
      {iframeElement}
    </AspectRatio>
  ));

  const showOverlay = overlayWhileLoading
    && Boolean(src)
    && (isRendering || Boolean(singleAdQuery?.isRefetching) || !iframeReady);

  const isBlocking = overlayWhileLoading
    ? !src
    : (singleAdQuery?.isRefetching || !src);

  const withOverlay = (content) => (
    <Box pos="relative" w="100%">
      {content}
      {showOverlay && (
        <Box style={RENDER_OVERLAY_STYLE}>
          <Loader color="smartico-pink.6" size="md" />
        </Box>
      )}
    </Box>
  );

  if (fitToWidth) {
    return (
      <Box ref={stageRef} w="100%">
        {isBlocking ? (
          <Center h={height}>
            <Loader color="gray" />
          </Center>
        ) : (
          withOverlay(iframePreview)
        )}
      </Box>
    );
  }

  if (formatType.includes('billboard')) {
    return (
      <Box miw={width} width="100%" mih={250}>
        {isBlocking ? (
          <Center h={250}>
            <Loader color="gray" />
          </Center>
        ) : (
          withOverlay(iframePreview)
        )}
      </Box>
    );
  }


  const screenBox = (
    <Box miw={300} width={300} mih={600} p={0}>
      {isBlocking ? (
        <Center h={607}>
          <Loader color="gray" />
        </Center>
      ) : (
        withOverlay(iframePreview)
      )}
    </Box>
  );

  if (!withFrame) {
    return (
      <Box miw={width} width={width} p={0}>
        {isBlocking ? (
          <Center h={height}>
            <Loader color="gray" />
          </Center>
        ) : (
          withOverlay(iframePreview)
        )}
      </Box>
    );
  }

  return <PhoneFrame>{screenBox}</PhoneFrame>;
}

export const AdPreview = React.memo(AdPreviewTmp);
